export const ROUTES = {
  home: '/',
  /** The Campaigns list. It moved off `/` when Home took the index route (NPJB-899). */
  campaigns: '/campaigns',
  campaignDetail: (id: string) => `/campaigns/${id}`,
  audienceBuilder: '/audience-builder',
  campaignCreate: '/campaigns/new',
  audienceBuilderWithId: (id: string) => `/audience-builder/${id}`,
  audienceProducts: '/audience-products',
  audienceProductsWithId: (id: string) => `/audience-products/${id}`,
  audienceContent: '/audience-content',
  audienceContentWithId: (id: string) => `/audience-content/${id}`,
  audienceReview: '/audience-review',
  audienceReviewWithId: (id: string) => `/audience-review/${id}`,
  audienceRequestConfirmation: (id: string) => `/audience-request-confirmation/${id}`,
  audienceOverview: (id: string) => `/audiences/${id}`,
  audienceEdit: (id: string) => `/audiences/${id}/edit`,
  login: '/login',
  signup: '/signup',
  /**
   * The flow's entry point, not a screen: it resolves `GET /onboarding/state`
   * and forwards to whichever step that account is actually on.
   */
  onboarding: '/onboarding',
  onboardingSignup: '/onboarding/signup',
  onboardingVerify: '/onboarding/verify',
  onboardingWelcome: '/onboarding/welcome',
  /** Activating a play picks its campaign here, then opens the Audience Builder. */
  intelligenceCampaign: '/intelligence/campaign',
  /**
   * NPJB-586. Lands on the default audience; the page itself lives at `intelligenceAudience`.
   * Behind `IntelligenceProfileGuard` (`intelligence-audience-switcher` only) — check
   * `useIntelligenceAudienceAccess` before routing here.
   */
  intelligenceAudiences: '/intelligence/audiences',
  /** NPJB-586. The audience page — the audience travels in the path. */
  intelligenceAudience: (audienceId: string) => `/intelligence/audiences/${audienceId}`,
  /**
   * NPJB-676. The audience travels in the path so the page reproduces the surface it was
   * opened from (AC-2). `domHash` is a 64-bit id — pass it as the string it arrived as.
   */
  accountProfile: (audienceId: string, domHash: string) =>
    `/intelligence/audiences/${audienceId}/accounts/${domHash}`,
  /**
   * NPJB-899 → NPJB-848. A general account profile with no audience, opened from Home search.
   * The page itself is NPJB-848's work; until it ships this path is the app's not-found page.
   */
  homeAccountProfile: (domHash: string) => `/intelligence/accounts/${domHash}?from=home`,
  /** NPJB-679. Same string as contact-profile's `contactProfilePath`. */
  contactProfile: (audienceId: string, domHash: string, personKey: string) =>
    `/intelligence/audiences/${audienceId}/accounts/${domHash}/contacts/${personKey}`,
} as const;

/**
 * Canonical landing route for an audience request given its current status.
 * Used by the confirmation, overview, and wizard pages to keep the user on
 * the right page when status flips (e.g. paste an `avails_ready` URL into
 * the wizard, land on the overview).
 *
 * Accepts both wire (`avails_ready`) and domain (`availsReady`) casings.
 */
export const getAudienceRequestRoute = (status: string, id: string): string => {
  switch (status) {
    // Post-finalisation states all land on the audience overview — once the
    // audience data exists, the user is reading/managing it, not editing.
    case 'avails_ready':
    case 'availsReady':
    case 'activating':
    case 'campaign_active':
    case 'campaignActive':
    case 'delivered':
    case 'paused':
    case 'archived':
      return ROUTES.audienceOverview(id);
    case 'building_audience':
    case 'buildingAudience':
      return ROUTES.audienceRequestConfirmation(id);
    case 'draft':
    default:
      return ROUTES.audienceReviewWithId(id);
  }
};

/**
 * One page of the onboarding flow, pinned to an ICP (NPJB-726).
 *
 * This is how a seller edits their ICP from Intelligence: the same steps, but
 * every read and write goes to the ICP named here rather than the newest one.
 * `page` is a step key or `welcome`.
 */
export const onboardingIcpPath = (icpId: string, page: string): string =>
  `/onboarding/${icpId}/${page}`;
